import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Files, Settings, Trash2, Download } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/store/auth';
import { templatesApi } from '@/lib/api';

export default function FacilityTemplatesTab() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const facilityId = user?.facilityId || '';

  const { data: templatesResponse, isLoading, error } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templatesApi.list(),
  });

  const templatesData = templatesResponse?.templates || [];

  const deleteTemplateMutation = useMutation({
    mutationFn: templatesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      alert('Template deleted successfully.');
    },
    onError: (error: any) => {
      alert(`Error: ${error.message || 'Failed to delete template.'}`);
    },
  });

  const applyTemplateMutation = useMutation({
    mutationFn: templatesApi.apply,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['facility-assets'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      alert('Template applied successfully! Your assets have been updated.');
    },
    onError: (error: any) => {
      alert(`Error: ${error.message || 'Failed to apply template.'}`);
    },
  });

  const handleDeleteTemplate = (templateId: number, templateName: string) => {
    if (confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
      deleteTemplateMutation.mutate(templateId);
    }
  };

  const handleApplyTemplate = (templateId: number, templateName: string) => {
    if (confirm(`Apply template "${templateName}"? This will update your current asset configuration.`)) {
      applyTemplateMutation.mutate(templateId);
    }
  };

  // Filter templates to only show ones for this facility
  const filteredTemplates = useMemo(() => {
    if (!templatesData || !facilityId) return [];
    return templatesData
      .filter((template: any) => template.facilityId === facilityId)
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [templatesData, facilityId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!facilityId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-red-600">Facility ID not found. Please contact administrator.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-red-600">Failed to load templates: {String(error)}</p>
          <Button 
            onClick={() => queryClient.invalidateQueries({ queryKey: ['templates'] })}
            variant="outline"
            className="mt-4"
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Files className="h-6 w-6" />
            Template Management
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage and apply saved asset configurations for {facilityId}
          </p>
        </div>
      </div>

      {/* Template Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {filteredTemplates?.length || 0}
              </p>
              <p className="text-sm text-muted-foreground">Your Templates</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {filteredTemplates?.reduce((sum, template) => sum + (template.templateAssets?.length || 0), 0) || 0}
              </p>
              <p className="text-sm text-muted-foreground">Total Assets</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {filteredTemplates?.reduce((sum, template) => sum + (template.templateAssets?.reduce((assetSum: number, asset: any) => assetSum + asset.duration, 0) || 0), 0) || 0}s
              </p>
              <p className="text-sm text-muted-foreground">Total Duration</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates List */}
      {!filteredTemplates || filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Files className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No templates found</h3>
            <p className="text-muted-foreground mb-4">
              Create your first template by saving a slideshow configuration from the Assets tab.
            </p>
            <div className="text-sm text-muted-foreground">
              <p>Templates allow you to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Save current asset configurations</li>
                <li>Quickly restore previous setups</li>
                <li>Maintain consistent slideshow configurations</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredTemplates.map((template: any) => (
            <Card key={template.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Files className="h-5 w-5" />
                      {template.name}
                    </CardTitle>
                    <CardDescription>
                      {template.description || 'No description provided'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{template.facilityId}</Badge>
                    <Badge variant="outline">
                      {template.templateAssets?.length || 0} assets
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Assets</p>
                    <p className="text-lg font-semibold">{template.templateAssets?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Duration</p>
                    <p className="text-lg font-semibold">
                      {template.templateAssets?.reduce((sum: number, asset: any) => sum + asset.duration, 0) || 0}s
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Created</p>
                    <p className="text-sm">{formatDate(template.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Updated</p>
                    <p className="text-sm">{formatDate(template.updatedAt)}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleApplyTemplate(template.id, template.name)}
                    disabled={applyTemplateMutation.isPending}
                    className="flex items-center gap-1"
                  >
                    <Download className="h-4 w-4" />
                    Apply Template
                  </Button>
                  
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteTemplate(template.id, template.name)}
                    disabled={deleteTemplateMutation.isPending}
                    className="flex items-center gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Template Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">How Templates Work</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Templates save your current asset configuration</li>
                <li>• Includes which assets are active and their durations</li>
                <li>• Apply templates to quickly restore previous setups</li>
                <li>• Templates are specific to your facility ({facilityId})</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Creating Templates</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Go to Assets & Slideshow tab</li>
                <li>• Configure your assets and durations</li>
                <li>• Click "Save as Template" button</li>
                <li>• Templates appear here for future use</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
